import { Suspense, useEffect, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CaretLeft, CaretRight } from "@phosphor-icons/react"
import { cva } from "class-variance-authority"
import { Button } from "@/components/ui/button"
import { DateOnly } from "@/lib/date-only"
import { formatDate } from "@/lib/format-date"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { cn } from "@/lib/utils"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { GetAvailableDatesCapacityInput } from "@/modules/patients/services/__generated__/useAgentAvailableDatesForWeekQuery.graphql"
import { useAgentAvailableDatesForWeekQuery } from "@/modules/patients/services/useAgentAvailableDatesForWeekQuery"

const DAYS_COUNT = 7
const WEEKS_COUNT = 3
const WEEKDAY_FORMAT = "EEE"
const DAY_NUMBER_FORMAT = "d"

type AgentAvailabilityIndicatorProps = {
  filters: GetAvailableDatesCapacityInput
  onSelectDate: (date: DateOnly) => void
}

type AgentAvailabilityContentProps = AgentAvailabilityIndicatorProps & {
  weekIndex: number
}

const dayCellVariants = cva(
  "flex flex-col items-center gap-1 rounded-md border border-transparent px-2 py-2 text-sm transition-colors",
  {
    variants: {
      state: {
        available:
          "bg-success-surface-subdued text-on-success-surface hover:bg-success-surface-subdued-hovered cursor-pointer",
        unavailable: "bg-surface-subdued text-on-surface-disabled cursor-default",
        selected: "border-primary bg-primary-surface text-on-primary-surface cursor-pointer",
      },
    },
    defaultVariants: { state: "unavailable" },
  },
)

const AgentAvailabilitySkeleton = () => (
  <div className="grid grid-cols-7 gap-2">
    {Array.from({ length: DAYS_COUNT }, (_, index) => (
      <div key={index} className="bg-surface-subdued h-14 animate-pulse rounded-md" />
    ))}
  </div>
)

const AgentAvailabilityContent = ({ filters, weekIndex, onSelectDate }: AgentAvailabilityContentProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<TeleconsultationForm>()
  const selectedDate = useWatch({ control, name: "date" })

  const availableDates = useAgentAvailableDatesForWeekQuery(filters, weekIndex * DAYS_COUNT)
  const availableSet = new Set(availableDates)

  const today = getServerDateOnly()
  const days = Array.from({ length: DAYS_COUNT }, (_, index) => today.addDays(weekIndex * DAYS_COUNT + index))
  const hasAnyAvailable = availableSet.size > 0

  return (
    <>
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const isAvailable = availableSet.has(day.toString())
          const isSelected = day.equals(selectedDate)
          const state = isSelected ? "selected" : isAvailable ? "available" : "unavailable"

          return (
            <button
              key={day.toString()}
              type="button"
              disabled={!isAvailable}
              onClick={() => onSelectDate(day)}
              className={dayCellVariants({ state })}
              data-testid={`agent-availability-day-${day.toString()}`}
              aria-label={t(
                isAvailable
                  ? "patient.agenda.agentAvailability.available"
                  : "patient.agenda.agentAvailability.notAvailable",
              )}>
              <span className="font-normal capitalize">{formatDate(day.toDate(), WEEKDAY_FORMAT)}</span>
              <span className="text-base font-semibold">{formatDate(day.toDate(), DAY_NUMBER_FORMAT)}</span>
            </button>
          )
        })}
      </div>
      {!hasAnyAvailable && (
        <span className="text-on-surface-disabled text-sm">
          {t("patient.agenda.agentAvailability.noSlotsThisWeek")}
        </span>
      )}
    </>
  )
}

export const AgentAvailabilityIndicator = ({ filters, onSelectDate }: AgentAvailabilityIndicatorProps) => {
  const { t } = useTranslation()
  const [weekIndex, setWeekIndex] = useState(0)

  useEffect(() => setWeekIndex(0), [filters])

  const isFirstWeek = weekIndex === 0
  const isLastWeek = weekIndex === WEEKS_COUNT - 1

  return (
    <div className="flex flex-col gap-2" data-testid="agent-availability-indicator">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t("patient.agenda.agentAvailability.title")}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="tertiary"
            className="p-2"
            data-testid="agent-availability-prev-week"
            disabled={isFirstWeek}
            aria-label={t("patient.agenda.agentAvailability.previousWeek")}
            onClick={() => setWeekIndex((week) => Math.max(0, week - 1))}>
            <CaretLeft className={cn("h-6 w-6", isFirstWeek ? "text-on-surface-disabled" : "text-on-surface")} />
          </Button>
          <Button
            variant="tertiary"
            className="p-2"
            data-testid="agent-availability-next-week"
            disabled={isLastWeek}
            aria-label={t("patient.agenda.agentAvailability.nextWeek")}
            onClick={() => setWeekIndex((week) => Math.min(WEEKS_COUNT - 1, week + 1))}>
            <CaretRight className={cn("h-6 w-6", isLastWeek ? "text-on-surface-disabled" : "text-on-surface")} />
          </Button>
        </div>
      </div>
      <Suspense fallback={<AgentAvailabilitySkeleton />}>
        <AgentAvailabilityContent filters={filters} weekIndex={weekIndex} onSelectDate={onSelectDate} />
      </Suspense>
    </div>
  )
}
